import { useFormikContext } from "formik";
import { useMemo } from "react";

import { FieldInputNumber, FieldLabel, FieldSelect, FormRowField } from "@skilitics/designer-ui-components";
import { isTernaryOperator, numberOperators } from "@skilitics/judy";
import { useFormatMessage } from "@skilitics/localization";
import { isReferencedValue } from "@skilitics/nautilus-core-designer-data";

import { useTriggerScopeNodeId } from "../../../triggers/HandlerDesignerContext.ts";
import { defaultNumberLiteral } from "../../subtype-definitions/numberSpec.ts";
import type { ComparisonViewProps, ComparisonFieldValues } from "../../valueSubtypeDefinition.ts";
import type { ValueSubtype } from "../../valueSubtypes.ts";
import type { NumberValueSubtypeSpec } from "../../valueSubtypeSpecs.ts";
import { ValueConfigEditor } from "../ValueConfigEditor.tsx";
import { useOperatorOptions } from "./useOperatorOptions.ts";

export type NumberComparisonViewProps = ComparisonViewProps<ValueSubtype, NumberValueSubtypeSpec> & {
  min?: number;
  max?: number;
  fractionDigits?: number;
  formatStringOverrides?: Partial<typeof numberComparisonFormatStrings>;
};

/**
 * The operator and operand rows of a number comparison. Rows only: it renders
 * inside whichever Form mounts it and opens none of its own.
 *
 * A host either hands it the subject's spec or passes the same fields as props.
 * A prop given wins, so a host that passes its own is unaffected by the spec.
 */
export const NumberComparisonView = ({
  subtypeSpec,
  scopeNodeId,
  excludeNodeId,
  bindable,
  disabled = false,
  min = subtypeSpec?.min,
  max = subtypeSpec?.max,
  fractionDigits = subtypeSpec?.fractionDigits,
  formatStringOverrides = subtypeSpec?.formatStringOverrides,
}: NumberComparisonViewProps) => {
  // Spec only, deliberately: a host that shifts its own operands at its form's
  // boundary would shift them twice if it could pass one here.
  const offset = subtypeSpec?.offset;
  const mergedFormatStrings = useMemo(
    () => ({ ...numberComparisonFormatStrings, ...formatStringOverrides }),
    [formatStringOverrides],
  );
  const formatMessage = useFormatMessage(mergedFormatStrings);
  const triggerScopeNodeId = useTriggerScopeNodeId();
  const { values } = useFormikContext<ComparisonFieldValues>();
  const operatorOptions = useOperatorOptions(numberOperators, values.operator);

  const ternary = !!values.operator && isTernaryOperator(values.operator);
  const editorProps = {
    type: "number",
    scopeNodeId: scopeNodeId ?? triggerScopeNodeId,
    excludeNodeId,
    bindable,
    // unbinding lands inside the range, not on a zero the field would reject
    defaultLiteral: defaultNumberLiteral(subtypeSpec),
  } as const;

  return (
    <>
      <FormRowField<ComparisonFieldValues> name="operator">
        <FieldLabel />
        <FieldSelect disabled={disabled} options={operatorOptions} placeholder={formatMessage("operatorPlaceholder")} />
      </FormRowField>

      <FormRowField<ComparisonFieldValues> name="value">
        <FieldLabel>{formatMessage(ternary ? "from" : "valueLabel")}</FieldLabel>
        <ValueConfigEditor {...editorProps} disabled={disabled}>
          <FieldInputNumber
            disabled={disabled}
            min={min}
            max={max}
            offset={offset}
            fractionDigits={fractionDigits}
            useMaxFractionDigits
            slotProps={{ input: { endAdornment: formatMessage("unitsSuffix") } }}
          />
        </ValueConfigEditor>
      </FormRowField>

      {ternary && (
        <FormRowField<ComparisonFieldValues> name="value2">
          <FieldLabel />
          <ValueConfigEditor {...editorProps} disabled={disabled}>
            <FieldInputNumber
              disabled={disabled}
              // the range opens at whatever "from" holds, so a literal pair can't cross over
              min={isReferencedValue(values.value) ? min : (values.value as number | undefined)}
              max={max}
              offset={offset}
              fractionDigits={fractionDigits}
              useMaxFractionDigits
              slotProps={{ input: { endAdornment: formatMessage("unitsSuffix") } }}
            />
          </ValueConfigEditor>
        </FormRowField>
      )}
    </>
  );
};

/** Spread into the mounting form's `formatStrings` — an implicit `FieldLabel` reads its text from there. */
export const numberComparisonFormatStrings = {
  operatorLabel: {
    en: "Operator",
  },
  valueLabel: {
    en: "Value",
  },
  from: {
    en: "From",
  },
  value2Label: {
    en: "To",
  },
  operatorPlaceholder: {
    en: "Select an operator",
  },
  unitsSuffix: {
    en: "",
  },
};

export type NumberComparisonFormatStrings = typeof numberComparisonFormatStrings;
